import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Sparkles, UserPlus, Zap, Building2, Phone, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", company: "", email: "", linkedin: "" });

  const load = () => Promise.all([api.get("/leads"), api.get("/agents")]).then(([l, a]) => { setLeads(l.data); setAgents(a.data); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const seed = async () => {
    await api.post("/leads/seed");
    toast.success("6 demo leads added");
    load();
  };

  const add = async () => {
    if (!form.name || !form.company) return toast.error("Name and company required");
    await api.post("/leads", form);
    toast.success("Lead enriched in real time");
    setOpen(false);
    setForm({ name: "", title: "", company: "", email: "", linkedin: "" });
    load();
  };

  const streamDraft = async (lead) => {
    if (!agents.length) return toast.error("Create an AI agent first");
    setSelected(lead);
    setDraft("");
    setDrafting(true);
    try {
      const url = `${API}/ai/draft/stream?agent_id=${agents[0].agent_id}&lead_id=${lead.lead_id}&step_type=email`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          const evLine = lines.find((l) => l.startsWith("event:"));
          const dataLine = lines.find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const ev = evLine?.slice(6).trim();
          const payload = dataLine.slice(5).trim();
          try {
            const parsed = JSON.parse(payload);
            if (ev === "delta" && parsed.text) setDraft((d) => d + parsed.text);
          } catch (err) { console.warn("Skipping malformed SSE chunk", err); }
        }
      }
    } catch (e) {
      console.error("Draft stream failed", e);
      toast.error("Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Leads & real-time enrichment</h1>
          <p className="mt-1 text-sm text-slate-400">Every prospect enriched instantly with intent signals, tech stack and mobile.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={seed} variant="outline" data-testid="leads-seed-btn" className="border-white/10 bg-white/[0.02] hover:bg-white/[0.05]">
            <Sparkles className="w-4 h-4 mr-1" /> Seed demo leads
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="leads-add-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full">
                <UserPlus className="w-4 h-4 mr-1" /> Add lead
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0B1020] border-white/10 text-slate-100 max-w-md">
              <DialogHeader><DialogTitle>Add a prospect</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {["name", "title", "company", "email", "linkedin"].map((f) => (
                  <div key={f}><Label className="capitalize">{f}</Label><Input value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`lead-form-${f}`} className="bg-white/[0.03] border-white/10 mt-1" /></div>
                ))}
                <Button onClick={add} data-testid="lead-form-submit" className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold">Enrich & add</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-2">
          {leads.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500" data-testid="leads-empty-state">
              No leads yet. Click <strong>Seed demo leads</strong> or head to <strong>Prospecting</strong> to search.
            </div>
          )}
          {leads.map((l) => (
            <button key={l.lead_id} onClick={() => streamDraft(l)} data-testid={`lead-row-${l.lead_id}`} className="w-full text-left rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-sky-400/30 transition p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500/30 to-violet-500/30 grid place-items-center text-sm font-semibold">
                    {l.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{l.name}</div>
                    <div className="text-xs text-slate-500 truncate">{l.title} · {l.company}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/20 font-mono text-[10px]">Intent {l.enrichment?.intent_score}</Badge>
                  <Zap className="w-4 h-4 text-sky-300" />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {l.enrichment?.tech_stack?.map((t) => <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300">{t}</span>)}
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300">{l.enrichment?.employees}</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300">{l.enrichment?.revenue_estimate}</span>
              </div>
              <div className="mt-2 text-xs text-emerald-300 truncate">↳ {l.enrichment?.recent_signal}</div>
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 h-fit sticky top-20" data-testid="lead-detail-panel">
          {!selected ? (
            <div className="text-sm text-slate-500 text-center py-10">Click a lead to stream a live AI draft.</div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Selected prospect</div>
                <div className="mt-1 font-semibold">{selected.name}</div>
                <div className="text-xs text-slate-400">{selected.title} · {selected.company}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-white/[0.03] p-2"><div className="text-slate-500">Region</div><div>{selected.enrichment?.region}</div></div>
                <div className="rounded-lg bg-white/[0.03] p-2"><div className="text-slate-500">Industry</div><div>{selected.enrichment?.industry}</div></div>
                <div className="rounded-lg bg-white/[0.03] p-2 flex items-center gap-2"><Phone className="w-3 h-3 text-slate-500" /><span>{selected.enrichment?.direct_mobile}</span></div>
                <div className="rounded-lg bg-white/[0.03] p-2 flex items-center gap-2"><Building2 className="w-3 h-3 text-slate-500" /><span>{selected.enrichment?.revenue_estimate}</span></div>
              </div>
              <div className="border-t border-white/5 pt-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                  <Mail className="w-3 h-3" /> AI draft
                  {drafting && <span className="flex items-center gap-1 text-emerald-300 normal-case font-mono"><Loader2 className="w-3 h-3 animate-spin" /> streaming</span>}
                </div>
                <Textarea readOnly value={draft} onChange={() => {}} data-testid="lead-draft-textarea" placeholder={drafting ? "" : "Waiting…"} className="bg-white/[0.03] border-white/10 min-h-[180px] text-sm" />
                {draft && !drafting && (
                  <Button data-testid="lead-draft-send" className="mt-3 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" onClick={() => toast.success("Queued for sending")}>
                    <Send className="w-4 h-4 mr-1" /> Queue for sending
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
